import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { AppDispatch } from "index";
import { toast } from "react-toastify";
import { createSelector } from "reselect";

import {
  fetchServiceSalesOrderById,
  createServiceSalesOrder,
  updateServiceSalesOrder,
} from "slices/sales_order/thunk";
import {
  fetchSubcategories,
  fetchMakes,
  fetchModels,
  fetchCategories,
} from "slices/enquiry/thunk";
// 4. Common Dropdown Thunks
import { fetchCompanyByDropDown } from "slices/Masters/company/thunk";
import { fetchAllUsersDropdown } from "slices/UserManagement/user/thunk";

import {
  SalesOrderFormData,
  salesOrderFormData,
} from "Common/CommonEnquiryData/SalesOrderForm";
// import { fetchAllDropdowns } from "slices/UserManagement/organisation/thunk";
import {
  fetchServiceQuotationById,
  fetchServiceQuotationDynamicDropdown,
} from "slices/quotation/thunk";
// import AddServicesSalesOrderForm from "./Services/Forms/SalesOrderServiceMasterForm";
import { clearAllTableStates } from "utils/clearTableStates";
import AddServicesSalesOrderForm from "./SalesOrderServiceMasterForm";

const ServicesSalesOrderForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  // --- State Management ---
  const [initialData, setInitialData] =
    useState<SalesOrderFormData>(salesOrderFormData);
  const [companies, setCompanies] = useState<any[]>([]);
  const [organizationsState, setOrganizationsState] = useState<any[]>([]);
  const [quotationReferences, setQuotationReferences] = useState<any[]>([]);
  
  // --- Redux Selectors ---

  // 1. Selectors for Item Dropdown state (Categories, Makes, Models)
  const selectItemDropdowns = createSelector(
    (state: any) => state.Enquiry,
    (enquiry) => ({
      categories: enquiry.categories,
      sub_categories: enquiry.subCategories,
      makes: enquiry.makes,
      models: enquiry.models,
      products: enquiry.products,
    })
  );

  const { categories, sub_categories, makes, models, products } =
    useSelector(selectItemDropdowns);
  // 2. Company Dropdown Data
  const selectCompanyData = createSelector(
    (state: any) => state.Company,
    (company) => ({ companiesData: company.companyList || [] })
  );
  const { companiesData } = useSelector(selectCompanyData);

  // 3. Users Dropdown Data (Assigned Members)
  const selecetedUsers = createSelector(
    (state: any) => state.users,
    (user) => ({
      usersFormDropDowns: user.userFormDropDowns || [],
    })
  );
  const { usersFormDropDowns } = useSelector(selecetedUsers);

  // --- Side Effects (Data Fetching) ---

  // Update local state when Redux data is ready
  // useEffect(() => {
  //   setCompanies(companiesData);
  //   handleFetchOrganization();
  // }, [companiesData]);

  // Initialization: Fetch all required data
  useEffect(() => {
    const init = async () => {
      dispatch(fetchCompanyByDropDown());
      // dispatch(fetchCategories("services")); // Fetch categories specific to services
      dispatch(fetchAllUsersDropdown());
      // You may need to dispatch a thunk to fetch sources if they are dynamic
      // try {
      //   const qrefs = await dispatch(
      //     fetchServiceQuotationDynamicDropdown("quotation_number")
      //   ).unwrap();

      //   // Assuming the API returns a list of objects with { id: string, enquiry_number: string }
      //   setQuotationReferences(qrefs?.data);
      // } catch (err) {
      //   console.error("Failed to load enquiry references:", err);
      // }
      // try {
      //   if (isEdit && id) {
      //     const salesOrder = await dispatch(
      //       fetchServiceSalesOrderById(id)
      //     ).unwrap();
      //     setInitialData(salesOrder);
      //   }
      // } catch (err) {
      //   console.error(
      //     "Failed to load initial Service Sales Order form data:",
      //     err
      //   );
      //   toast.error("Failed to load form data.");
      // }
    };
    init();
  }, [dispatch, isEdit, id]);

  // --- Dynamic Dropdown Fetchers (Passed as Prop) ---

  // Fetch subcategories when a category is selected
  // const fetchServiceSubcategories = (categoryId: string) =>
  //   dispatch(
  //     fetchSubcategories({ item_type: "services", categoryId: categoryId })
  //   );

  // // For Service, makes/models are typically not required, but include the handlers if the MasterForm requires them:
  // // NOTE: This assumes your API supports make/model filtering for services, otherwise, you can pass empty functions or omit.
  // const fetchServiceMakes = (params: {
  //   categoryId: string;
  //   subcategoryId: string;
  // }) => {
  //   // If needed, dispatch(fetchMakes({ item_type: "services", ...params }));
  // };

  // const fetchServiceModels = (params: {
  //   categoryId: string;
  //   subcategoryId: string;
  //   makeId: string;
  // }) => {
  //   // If needed, dispatch(fetchModels({ item_type: "services", ...params }));
  // };

  // const handleFetchOrganization = async () => {
  //   try {
  //     const resData = await dispatch(fetchAllDropdowns()).unwrap();
  //     setOrganizationsState(resData?.orgStateDropDown || []);
  //     console.log("organization Data : ", resData);
  //   } catch (err) {
  //     console.error("Failed to load enquiry references:", err);
  //   }
  // };

  // --- API Handler (Passed as Prop) ---
  const handleSaveServiceSalesOrder = async (
    payload: any,
    action: "draft" | "print" | "save"
  ) => {
    try {
      if (isEdit && id) {
        await dispatch(updateServiceSalesOrder({ id, payload })).unwrap();
        toast.success("Service Sales Order updated successfully");
      } else {
        await dispatch(
          createServiceSalesOrder({ ...payload, action })
        ).unwrap();
         clearAllTableStates()
        toast.success("Service Sales Order created successfully");
      }
      // navigate("/sales_order/service/sales_order_log");
    } catch (err: any) {
      // Extract error message from various possible error structures
      const errorMessage = 
        err?.response?.data?.message || 
        err?.response?.data?.error || 
        err?.response?.data ||
        err?.message || 
        (typeof err === 'string' ? err : "Service Sales Order operation failed");
      
      // Throw the error for the MasterForm to handle loading state
      throw new Error(errorMessage);
    }
  };

  const handleQuotationSelect = async (quotationId: string) => {
    try {
      const resData = await dispatch(
        fetchServiceQuotationById(quotationId)
      ).unwrap();

      setInitialData(resData?.data); // Set the form data

    } catch (err: any) {
      // console.error("Failed to fetch enquiry data by reference:", err);
      toast.error(err?.message || err?.error || "Failed to load enquiry data.");
      setInitialData(salesOrderFormData); // Reset form on failure
    }
  };

  // --- Loading State Check (Crucial for Edit Mode) ---
  if (isEdit && !initialData) {
    // Check a nested detail to confirm data structure loaded
    return (
      <div className="d-flex justify-content-center mt-5">
        <p>Loading Sales Order...</p>
      </div>
    );
  }

  // Use a defined set of sources if not dynamically fetched
  const commonSources = [
    "Website",
    "Email",
    "Phone Call",
    "Referral",
    "Internal Reference",
  ];

  return (
    <div className="mt-10">
      <AddServicesSalesOrderForm
        isEdit={isEdit}
        id={id}
        initialData={initialData}
        companies={companiesData}
        // sources={commonSources}
        onSave={handleSaveServiceSalesOrder} // Service-specific handler
        enquiryReferences=""

        membersOptions={usersFormDropDowns} // Assigned member options
        organizationsState={organizationsState}
      />
    </div>
  );
};

export default ServicesSalesOrderForm;
